<script setup lang="ts">
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
import localStore from 'store';
import { type Component } from 'vue';
import { useRoute } from 'vue-router';

import IconChevronForward from '~icons/lucide/chevron-right';

import KSidebarLink from './KSidebarLink.vue';

export interface NavigationItem {
  display: string;
  routeName?: string;
  icon?: Component;
  current?: boolean;
  children?: NavigationItem[];
}

const route = useRoute();

const props = withDefaults(
  defineProps<{
    items: NavigationItem[];
    useProxy?: boolean;
    mobile?: boolean;
  }>(),
  { useProxy: true, mobile: false },
);

const OPEN_ITEMS_KEY = 'k10.sidebar.openItems';

let localStorageOpenItems: string[] = localStore.get(OPEN_ITEMS_KEY) || [];

const IDType = props.mobile === true ? 'Mobile' : '';

function isCurrent(item: NavigationItem) {
  if (item.routeName) return route.name === item.routeName;
  if (item.children) return item.children.some(isCurrent);
  return false;
}

function isOpen(display: string) {
  return localStorageOpenItems.includes(display);
}

function handleDisclosureClick(display: string) {
  if (localStorageOpenItems.includes(display)) {
    localStorageOpenItems = localStorageOpenItems.filter((d) => d !== display);
  } else {
    localStorageOpenItems = [display, ...localStorageOpenItems];
  }
  localStore.set(OPEN_ITEMS_KEY, localStorageOpenItems);
}
</script>

<template>
  <ul class="ul-reset -mx-2 mt-[2px] space-y-1">
    <li v-for="item in props.items" :key="item.display">
      <!-- SINGLE ITEM -->
      <KSidebarLink
        v-if="!item.children && item.routeName"
        :id="`sidebarLink${IDType}${item.display}`"
        :item="item"
        :use-proxy="props.useProxy"
        :current="isCurrent(item)"
      >
        <component
          :is="item.icon"
          :class="[isCurrent(item) ? 'text-current' : 'text-gray-600', 'h-6 w-6 shrink-0']"
          aria-hidden="true"
        />
        {{ item.display }}
      </KSidebarLink>

      <!-- MULTIPLE ITEMS -->
      <Disclosure v-else as="div" v-slot="{ open }" :default-open="isOpen(item.display)">
        <DisclosureButton
          :id="`sidebarButton${IDType}${item.display}`"
          :class="[
            isCurrent(item)
              ? 'bg-gray-50 dark:bg-navy-900'
              : 'hover:bg-gray-50 dark:hover:bg-navy-900',
            'flex w-full items-center gap-x-3 rounded-md p-2 text-left text-sm leading-6 text-gray-700 focus-visible:outline-2 focus-visible:outline-blue-400 dark:text-gray-400',
          ]"
          @click="handleDisclosureClick(item.display)"
        >
          <component :is="item.icon" class="h-6 w-6 shrink-0 text-gray-600" aria-hidden="true" />
          {{ item.display }}
          <IconChevronForward
            :class="[
              open && 'rotate-90',
              'ml-auto h-5 w-5 shrink-0 text-gray-600 transition-transform',
            ]"
            aria-hidden="true"
          />
        </DisclosureButton>
        <DisclosurePanel as="ul" class="ul-reset mt-1 px-2">
          <li v-for="subItem in item.children" :key="subItem.display">
            <KSidebarLink
              :id="`sidebarSubLink${IDType}${subItem.display}`"
              :item="subItem"
              :use-proxy="props.useProxy"
              :sub-item="true"
              :current="isCurrent(subItem)"
            />
          </li>
        </DisclosurePanel>
      </Disclosure>
    </li>
  </ul>
</template>
